import { IFavouriteMutation, spLog } from "@eventr/common";
import axios from "axios";
import { StateCreator } from "zustand";
import { DataAPI } from "../data/Data";
import { useConnectivityStore } from "../feat/connectivity/connectivity.store";
import {
  FavouriteMap,
  applyFavouriteMutations,
  isFavourited,
  reconcileFavourites,
} from "../feat/favourites/favourite-state";
import { favouriteOutbox } from "../feat/favourites/favourite-outbox";
import { BoundStore } from "./bound.store";

interface FavouritesState {
  /** Keyed by entry id. See FavouriteMap for why this is not a Set. */
  favourites: FavouriteMap;
}

interface FavouritesActions {
  isEntryFavourited: (entryId: string) => boolean;
  /** Applies the change locally, then queues it. Safe with no connection. */
  toggleFavourite: (itineraryId: number, entryId: string) => Promise<void>;
  /** Fetches the server's list, then folds anything still queued on top of it. */
  loadFavourites: () => Promise<void>;
  /** Replays anything queued and folds the server's answer back in. */
  syncFavourites: () => Promise<void>;
  clearFavourites: () => void;
}

export interface FavouritesSlice extends FavouritesState, FavouritesActions {}

export const createFavouritesSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], FavouritesSlice> = (
  set,
  get,
) => ({
  favourites: {},

  isEntryFavourited: (entryId) => isFavourited(get().favourites, entryId),

  toggleFavourite: async (itineraryId, entryId) => {
    const mutation: IFavouriteMutation = {
      itineraryId,
      entryId,
      favourited: !get().isEntryFavourited(entryId),
      // Stamped when the user taps, so a change replayed later cannot undo a newer one.
      at: new Date().toISOString(),
    };

    set({ favourites: applyFavouriteMutations(get().favourites, [mutation]) }, false, "toggleFavourite");

    await favouriteOutbox.queue(get().selectedOrg?.name ?? "", mutation);
    await get().syncFavourites();
  },

  loadFavourites: async () => {
    if (!useConnectivityStore.getState().isConnectivityAvailable) return;

    try {
      const server = await DataAPI.favourites.getAll();
      const stillQueued = await favouriteOutbox.pending(get().selectedOrg?.name ?? "");

      set({ favourites: reconcileFavourites(server, stillQueued) }, false, "loadFavourites");
    } catch (error) {
      // The persisted copy stands in until the next attempt.
      spLog("Favourite load deferred", error, ["FAVOURITES"]);
    }
  },

  syncFavourites: async () => {
    // guestAbortRequestInterceptor cancels every request while offline. Attempting the sync
    // anyway would burn a retry per tap and eventually discard unrejected changes.
    if (!useConnectivityStore.getState().isConnectivityAvailable) return;

    const org = get().selectedOrg?.name ?? "";
    const queued = await favouriteOutbox.pending(org);
    if (queued.length === 0) return;

    try {
      const server = await DataAPI.favourites.sync(queued);
      // Anything queued while the request was in flight has to survive the answer.
      const stillQueued = await favouriteOutbox.settle(org, queued);

      set({ favourites: reconcileFavourites(server, stillQueued) }, false, "syncFavourites");
    } catch (error) {
      // Silent by design. The change is already on screen and stays queued.
      spLog("Favourite sync deferred", error, ["FAVOURITES"]);

      // Only a refusal counts against a change. A cancelled request never reached the server.
      if (!axios.isCancel(error)) {
        await favouriteOutbox.recordFailure(org);
      }
    }
  },

  clearFavourites: () => {
    set({ favourites: {} }, false, "clearFavourites");
    void favouriteOutbox.clear();
  },
});
